import { useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import type { DisplayedMetrics } from '../components/creatives/data/displayedMetrics';
import type { ScopeState } from '../components/filters/types';
import { DEFAULT_KPI, DEFAULT_TOP_MARKETS, toTopMarkets } from '../components/media/data/constants';
import type { MediaFilters } from '../components/media/data/types';
import { kpiById } from '../components/media/data/utils';
import { scopeFromSearch, scopeSearchShape, serializeScope } from './scope-search';
import { commaSeparatedArray } from './search-codec';

const mediaFiltersSchema = z
  .object({
    countries: commaSeparatedArray.optional().catch(undefined),
    creativeTypes: commaSeparatedArray.optional().catch(undefined),
    objectives: commaSeparatedArray.optional().catch(undefined),
    kpi: z.string().optional().catch(undefined),
    topMarkets: z.coerce.number().optional().catch(undefined),
  })
  .catch({});

const displayedMetricsSchema = z
  .object({
    columns: commaSeparatedArray.optional().catch(undefined),
    sort: z.string().optional().catch(undefined),
    dir: z.enum(['asc', 'desc']).optional().catch(undefined),
    kpi: z.string().optional().catch(undefined),
  })
  .catch({});

export const mediaSearchSchema = z
  .object({
    ...scopeSearchShape,
    filters: mediaFiltersSchema.optional().catch(undefined),
    metrics: displayedMetricsSchema.optional().catch(undefined),
    view: z.enum(['table', 'grid']).optional().catch(undefined),
  })
  .catch({});

export type BoardName = 'dashboard' | 'media' | 'explore' | 'elements';
export type BoardRoute = '/dashboard' | '/media' | '/explore' | '/elements-explorer';
export type BoardView = 'table' | 'grid';
export type MediaFiltersSearch = z.infer<typeof mediaFiltersSchema>;
export type DisplayedMetricsSearch = z.infer<typeof displayedMetricsSchema>;
export type MediaSearch = z.infer<typeof mediaSearchSchema>;

function carriesValue(part: object | undefined): boolean {
  return part !== undefined && Object.values(part).some((value) => value !== undefined);
}

export function validateMediaSearch(search: Record<string, unknown>): MediaSearch {
  const parsed = mediaSearchSchema.parse(search);
  const result: MediaSearch = {};
  if (parsed.brand !== undefined) result.brand = parsed.brand;
  if (parsed.platform !== undefined) result.platform = parsed.platform;
  if (parsed.account !== undefined) result.account = parsed.account;
  if (parsed.creativeType !== undefined) result.creativeType = parsed.creativeType;
  if (parsed.range !== undefined) result.range = parsed.range;
  if (carriesValue(parsed.filters)) result.filters = parsed.filters;
  if (carriesValue(parsed.metrics)) result.metrics = parsed.metrics;
  if (parsed.view !== undefined) result.view = parsed.view;
  return result;
}

function serializeFilters(filters: MediaFilters): MediaFiltersSearch | undefined {
  const search: MediaFiltersSearch = {};
  if (filters.countries.length) search.countries = filters.countries;
  if (filters.creativeTypes.length) search.creativeTypes = filters.creativeTypes;
  if (filters.objectives.length) search.objectives = filters.objectives;
  if (filters.kpi !== DEFAULT_KPI) search.kpi = filters.kpi;
  if (filters.topMarkets !== DEFAULT_TOP_MARKETS) search.topMarkets = filters.topMarkets;
  return Object.keys(search).length ? search : undefined;
}

function filtersFromSearch(search: MediaFiltersSearch | undefined): MediaFilters {
  return {
    countries: search?.countries ?? [],
    creativeTypes: search?.creativeTypes ?? [],
    objectives: search?.objectives ?? [],
    kpi: kpiById(search?.kpi ?? DEFAULT_KPI).id,
    topMarkets: toTopMarkets(search?.topMarkets),
  };
}

function metricsFromSearch(
  search: DisplayedMetricsSearch | undefined,
): DisplayedMetrics | undefined {
  if (!search?.columns?.length || search.sort === undefined || search.dir === undefined) {
    return undefined;
  }
  return { metrics: search.columns, sort: search.sort, direction: search.dir };
}

function viewFromSearch(view: BoardView | undefined): BoardView {
  return view ?? 'grid';
}

export function nextSearch(
  previous: Record<string, unknown>,
  override: {
    scope?: ScopeState;
    filters?: MediaFilters;
    metrics?: DisplayedMetrics;
    view?: BoardView;
  },
): Record<string, unknown> {
  const parsed = previous as MediaSearch;
  const scope = override.scope ?? scopeFromSearch(parsed);
  const filters = override.filters ?? filtersFromSearch(parsed.filters);
  const metrics = override.metrics ?? metricsFromSearch(parsed.metrics);
  const view = override.view ?? viewFromSearch(parsed.view);

  const search: Record<string, unknown> = { ...previous };
  const serializedScope = serializeScope(scope);
  for (const key of Object.keys(scopeSearchShape) as (keyof typeof scopeSearchShape)[]) {
    const value = serializedScope?.[key];
    if (value !== undefined) search[key] = value;
    else delete search[key];
  }
  const serializedFilters = serializeFilters(filters);
  if (serializedFilters) search['filters'] = serializedFilters;
  else delete search['filters'];
  // `kpi` is the leading KPI the sort was chosen under; a different one re-ranks the board.
  // Changing the leading KPI voids it ('' matches none), so no older sort comes back.
  const leadingKpiChanged = scope.leadingKpi !== scopeFromSearch(parsed).leadingKpi;
  const rankedFor = override.metrics
    ? scope.leadingKpi
    : leadingKpiChanged
      ? ''
      : parsed.metrics?.kpi;
  if (metrics) {
    search['metrics'] = {
      columns: metrics.metrics,
      sort: metrics.sort,
      dir: metrics.direction,
      ...(rankedFor === undefined ? {} : { kpi: rankedFor }),
    };
  } else {
    delete search['metrics'];
  }
  if (view !== 'grid') search['view'] = view;
  else delete search['view'];
  return search;
}

export function useMediaSearch(to: BoardRoute = '/media') {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as MediaSearch;

  return {
    filters: filtersFromSearch(search.filters),
    scope: scopeFromSearch(search),
    hasScope: carriesValue({
      brand: search.brand,
      platform: search.platform,
      account: search.account,
      range: search.range,
    }),
    displayedMetrics: metricsFromSearch(search.metrics),
    rankedFor: search.metrics?.kpi,
    view: viewFromSearch(search.view),
    setFilters: (filters: MediaFilters): void => {
      void navigate({
        to,
        replace: true,
        search: (previous) => nextSearch(previous as Record<string, unknown>, { filters }),
      });
    },
    setDisplayedMetrics: (metrics: DisplayedMetrics): void => {
      void navigate({
        to,
        replace: true,
        search: (previous) => nextSearch(previous as Record<string, unknown>, { metrics }),
      });
    },
    setScope: (scope: ScopeState): void => {
      void navigate({
        to,
        replace: true,
        search: (previous) => nextSearch(previous as Record<string, unknown>, { scope }),
      });
    },
    setView: (view: BoardView): void => {
      void navigate({
        to,
        replace: true,
        search: (previous) => nextSearch(previous as Record<string, unknown>, { view }),
      });
    },
  };
}
